import { useRef, useState, useEffect } from "react";

interface ImageUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectFile: (file: File) => void;
}

export default function ImageUploadModal({ isOpen, onClose, onSelectFile }: ImageUploadModalProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    useEffect(() => {
        if (isOpen) setFile(null);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (selected) setFile(selected);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) setFile(dropped);
    };

    const handleUpload = () => {
        if (!file) return;
        onSelectFile(file);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] mx-4">
                {/* 헤더 */}
                <div className="flex items-center justify-between p-6 border-b border-[#f5f2f0]">
                    <h2 className="text-[16px] font-bold text-[#2a1f1a]">이미지 첨부</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
                </div>

                <div className="p-6">
                    {/* 파일명 + 등록 버튼 */}
                    <div className="flex gap-2 mb-4">
                        <input
                            className="flex-1 px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] text-[#2a1f1a] outline-none bg-[#f5f2f0]"
                            value={file ? file.name : ""}
                            readOnly
                            placeholder="파일을 선택하세요"
                        />
                        <button
                            onClick={handleUpload}
                            disabled={!file}
                            className="px-5 py-2 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            등록
                        </button>
                    </div>

                    {/* 드래그 영역 */}
                    <div
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                        className={`border-2 border-dashed rounded-xl h-36 flex flex-col items-center justify-center gap-3 transition-colors ${isDragOver ? "border-[#887267] bg-[#fdf8f0]" : "border-[#e8e0dc] bg-[#fafaf9]"}`}
                    >
                        <p className="text-[13px] text-gray-400 text-center">
                            첨부할 파일을 여기에 끌어다 놓거나,<br />파일을 직접 선택해주세요.
                        </p>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="px-4 py-1.5 border border-[#887267] text-[#887267] rounded-lg text-[12px] font-medium hover:bg-[#f5f2f0] transition-colors flex items-center gap-1.5"
                        >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                            </svg>
                            파일 선택
                        </button>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                    </div>
                </div>
            </div>
        </div>
    );
}